import { customFunctions } from "@/config/customFunction";
import { useModal } from "@/features/modal/ModalStackManager";
import { LocationAutoComplete } from "@/features/ui/autoComplete/location/location-autoComplate";
import { FormActionButtonGroup } from "@/features/ui/formActionButtonGroup/FormActionButtonGroup";
import { NewLotCreateInput } from "@/features/ui/newLotCreateInput/NewLotCreateInput";
import { MantineSelectableTable } from "@/features/ui/table/MantineSelectableTable";
import { useCreatePurchaseOrderItemsWithLot } from "@/hooks/useCreatePurchaseOrderItemsWithLot";
import { Flex, NumberInput, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  AuthSignupPost201ResponseEquipmentPurchaseOrderItem,
  ItemsGet200ResponseRowsInnerLotsInner,
  PurchaseOrdersGet200ResponseRowsInnerPurchaseOrderItemsInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { MantineReactTable, MRT_ColumnDef } from "mantine-react-table";
import { Dispatch, SetStateAction, useEffect, useMemo } from "react";
import { useOrderItemsIncoming } from "../hook/useOrderItemsIncoming";

export interface PurchaseOrderItemsSelectFormProps {
  order?: () => (PurchaseOrdersGet200ResponseRowsInnerPurchaseOrderItemsInner | undefined)[];
  orderItems?: AuthSignupPost201ResponseEquipmentPurchaseOrderItem[] | undefined;
  setLoading: Dispatch<SetStateAction<boolean>>;
}
const OrderIncoming = Object.assign(
  {},
  {
    TextInput: TextInput,
    NumberInput: NumberInput,
    NewLotCreateInput: NewLotCreateInput,
    LocationAutoComplete: LocationAutoComplete,
    FormActionButtonGroup: FormActionButtonGroup,
    MantineSelectableTable: MantineSelectableTable,
    MantineReactTable: MantineReactTable,
  },
);

export interface OrderIncomingFormProps
  extends PurchaseOrdersGet200ResponseRowsInnerPurchaseOrderItemsInner {
  lotId?: number;
  lotInfo?: {
    name: string | undefined;
    expiration: string | undefined;
  };
  targetLocationCode?: string;
  inputValue?: number;
}

export const OrderIncomingForm = ({
  order,
  orderItems,
  setLoading,
}: PurchaseOrderItemsSelectFormProps) => {
  const { closeModal } = useModal();

  const selectedItems =
    order == undefined
      ? (orderItems as AuthSignupPost201ResponseEquipmentPurchaseOrderItem[])
      : order()?.filter(
          (item): item is PurchaseOrdersGet200ResponseRowsInnerPurchaseOrderItemsInner =>
            item !== undefined,
        );
  const { orderItemsBulkSubmit } = useOrderItemsIncoming({ setLoading });
  const { updatedPurchaseOrderItemsWithLot } = useCreatePurchaseOrderItemsWithLot({
    items: selectedItems,
  });

  const form = useForm<{
    Items: OrderIncomingFormProps[];
  }>({
    initialValues: {
      Items: [],
    },
  });

  const usesLotName = form.values.Items.some((item) =>
    order
      ? item.item?.usesLotName
      : (item as AuthSignupPost201ResponseEquipmentPurchaseOrderItem)?.usesLotName,
  );
  const usesLotExpiration = form.values.Items.some((item) =>
    order
      ? item.item?.usesLotExpiration
      : (item as AuthSignupPost201ResponseEquipmentPurchaseOrderItem)?.usesLotExpiration,
  );
  const isLotCreationEnabled = !usesLotName && !usesLotExpiration;

  const lotDescription = (() => {
    if (usesLotName && usesLotExpiration) return "이름 + 만료일";
    if (usesLotName) return "이름";
    if (usesLotExpiration) return "만료일";
    return "관리 안함";
  })();

  const columns = useMemo<MRT_ColumnDef[]>(() => {
    const baseColumns = [
      {
        header: "품목명",
        accessorFn: (row: any) => row.item?.name ?? row.itemName ?? "",
      },

      {
        accessorKey: "quantity",
        header: "발주 수량",
      },
      {
        accessorKey: "sumOfDeliveredQuantity",
        header: "입고수량",
        size: 100,
        Cell: ({ row }: any) => {
          return <Text>{row.original.sumOfDeliveredQuantity ?? 0}</Text>;
        },
      },
      {
        accessorKey: "targetLocationCode",
        header: "입고 로케이션",
        Cell: ({ row }: any) => {
          const index = row.index;

          return (
            <LocationAutoComplete
              {...form.getInputProps(`Items.${index}.targetLocationCode`)}
              value={form.values.Items?.[index]?.targetLocationCode}
            />
          );
        },
      },
      {
        accessorKey: "lotId",
        header: "로트번호",
        size: 300,
        Cell: ({ row }: any) => {
          const index = row.index;

          return (
            <NewLotCreateInput
              lotDescription={lotDescription}
              disabled={isLotCreationEnabled}
              itemCode={form.values.Items?.[index]?.itemCode as string}
              newIdValue={form.values.Items?.[index]?.lotId as number}
              defaultValue={form.values.Items?.[index]?.lotId?.toString() ?? ""}
              onLotCreated={() =>
                handleLotCreation(
                  index,
                  row.original.lotInfo as ItemsGet200ResponseRowsInnerLotsInner,
                )
              }
              onRowChange={(lot) => {
                if (!lot || typeof lot !== "object") return;
                form.setFieldValue(`Items.${index}.lotId`, Number(lot.value ?? ""));
                form.setFieldValue(`Items.${index}.lotInfo`, {
                  name: lot.label ?? "",
                  expiration: lot.expiration === "null" ? undefined : lot.expiration,
                });
              }}
            />
          );
        },
      },
      {
        accessorKey: "inputValue",
        header: "수량",
        Cell: ({ row }: any) => {
          return (
            <NumberInput
              label="수량"
              withAsterisk
              onChange={(value) => {
                form.setFieldValue(`Items.${row.index}.inputValue`, value);
              }}
              max={Number(row.original.quantity)}
            />
          );
        },
      },
    ];
    // if (customFunctions.ADD_ORDER_AUTO_CREATE_LOT) {
    //   baseColumns.push(
    //     {
    //       accessorKey: "inputValue",
    //       header: "입고개수",
    //       Cell: ({ row }) => {
    //         const isSelected = row.getIsSelected();
    //         const index = row.index;

    //         return (
    //           <OrderIncoming.NumberInput
    //             max={Number(form.values.Items?.[index]?.inputEligibleQuantity)}
    //             min={1}
    //             disabled={!isSelected} // selectedItem이 없는 경우 비활성화
    //             {...form.getInputProps(`Items.${index}.inputValue`)}
    //             value={Number(form.values.Items?.[index]?.inputValue)}
    //           />
    //         );
    //       },
    //     },
    //     {
    //       accessorKey: "purchaseOrderItemQty",
    //       header: "분할개수",
    //       Cell: ({ row }) => {
    //         const isUsesLotName = row.original.item?.usesLotName;
    //         const isSelected = row.getIsSelected();
    //         const index = row.index;

    //         return isUsesLotName ? (
    //           <OrderIncoming.NumberInput
    //             placeholder="분할 개수"
    //             min={1}
    //             disabled={!isSelected} // selectedItem이 없는 경우 비활성화
    //             {...form.getInputProps(`Items.${index}.purchaseOrderItemQty`)}
    //             value={Number(form.values.Items?.[index]?.purchaseOrderItemQty)}
    //           />
    //         ) : (
    //           <Text color="red">{"로트가 존재하지 않아 분할을 하실 수 없습니다."}</Text>
    //         );
    //       },
    //     },
    //     {
    //       accessorKey: "lotName",
    //       header: "로트번호",
    //       Cell: ({ row }) => {
    //         const isUsesLotName = row.original.item?.usesLotName;

    //         return isUsesLotName ? (
    //           <Text>{row.original.lotInfo.name}</Text>
    //         ) : (
    //           <Text color="red">{"로트를 사용하지 않는 품목입니다."}</Text>
    //         );
    //       },
    //     },
    //   );
    // }

    return baseColumns;
  }, [customFunctions.ADD_ORDER_AUTO_CREATE_LOT, form.values.Items]);

  const handleLotCreation = (index: number, data: ItemsGet200ResponseRowsInnerLotsInner) => {
    form.setFieldValue("Items", [
      ...form.values.Items.slice(0, index),
      {
        ...form.values.Items[index],
        lotId: data.id,
        lotInfo: {
          name: data.name,
          expiration: data.expiration,
        },
      },
      ...form.values.Items.slice(index + 1),
    ]);
  };

  const onSubmit = () => {
    const filteredItems = (form.values.Items ?? []).filter((item) => item.item !== undefined);
    orderItemsBulkSubmit(filteredItems);
    closeModal(true);
  };

  useEffect(() => {
    if (customFunctions.ADD_ORDER_AUTO_CREATE_LOT) {
      form.setFieldValue("Items", updatedPurchaseOrderItemsWithLot);
    } else {
      form.setFieldValue(
        "Items",
        selectedItems?.map((item) => {
          return { ...item, inputValue: Number(item.inputEligibleQuantity) };
        }) as PurchaseOrdersGet200ResponseRowsInnerPurchaseOrderItemsInner[],
      );
    }
  }, [order, updatedPurchaseOrderItemsWithLot]);

  return (
    <>
      {selectedItems?.length === 0 ? (
        <Flex>
          <Text size="xl" fw="bold">
            발주항목이 없습니다. 발주항목을 생성해주세요.
          </Text>
        </Flex>
      ) : (
        <Flex direction={"column"} gap={30}>
          <OrderIncoming.MantineReactTable
            data={form.values.Items ?? []}
            columns={columns}
            enableColumnFilters={false}
            enableGlobalFilter={false}
            enableColumnActions={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableHiding={false}
            enablePagination={false}
          />
          <OrderIncoming.FormActionButtonGroup
            form={form}
            onCancel={() => closeModal({})}
            onSubmit={onSubmit}
          >
            {"입고"}
          </OrderIncoming.FormActionButtonGroup>
        </Flex>
      )}
    </>
  );
};
